import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindActionCreators } from 'redux';
import {
  Card,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { Loading } from '../../../../../loadbar/Loading';
import {
  clearTsunamiEventInfo as clearTsunamiEventInfoAc,
  getTsunamiEventInfo as getTsunamiEventInfoAc,
  getTsunamiEventReferences as getTsunamiEventReferencesAc,
  sortReferences as sortReferencesAc,
  toggleColumns as toggleColumnsAc,
} from '../../../../../../redux/tsunamis/event/info/tsunamiEventInfoActions';
import { parseAndShortenFloat } from '../../../../../../helperFunctions/helperFunctions';
import { ReferencesTable } from '../../../../../table/ReferencesTable';
import { SingleResultGrid } from '../../../../../grid/SingleResultGrid';
import { Column } from '../../../../../grid/Column';
import { CommentsSection } from '../../../../../form/CommentsSection';
import { EditIcon } from '../../../../../icon/EditIcon';
import { isDataManager } from '../../../../../../role/roleUtils';
import { TitleTooltip } from '../../../../../form/TitleToolTip';

const renderEditLink = id => (
  <Link aria-label={`Edit Tsunami Event ${id}`} to={`/view/hazards/tsunami/update-tsunami/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderRelatedRunupsLink = (id, value) => (
  <Link to={`/view/hazards/tsunami/related-runups/${id}`} target="_blank" rel="noopener noreferrer">
    {value}
  </Link>
);

const renderRelatedDepositsLink = (id, value) => (
  <Link to={`/view/hazards/tsunami/related-deposits/${id}`} target="_blank" rel="noopener noreferrer">
    {value}
  </Link>
);

class TsunamiInfoPageUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
  }

  componentDidMount() {
    document.title = 'NCEI Hazard Tsunami Event Information';
    const {
      match: { params }, getTsunamiEventReferences, getTsunamiEventInfo,
    } = this.props;
    getTsunamiEventReferences({ id: params.id });
    getTsunamiEventInfo({ id: params.id });
  }

  componentWillUnmount() {
    const { clearTsunamiEventInfo } = this.props;
    clearTsunamiEventInfo();
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortReferences, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortReferences(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      fetched, tsunami, references, sort, visibleColumns, match: { params: { id } }, authorities,
    } = this.props;

    const dataManager = isDataManager(authorities);

    if (!fetched) {
      return (<Loading />);
    }

    return (
      <div className="haz-results-container haz-page-container">
        <Card className="haz-page-card">
          <CardBody>
            <CardTitle tag="h1" className="haz-page-card-title">Tsunami Event Information</CardTitle>
            {dataManager && renderEditLink(id)}
            <div className="haz-info-content">
              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Tsunami Event Information</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="year" width={75} helpModalId="year">Year</Column>
                      <Column id="month" width={50} helpModalId="month">Mo</Column>
                      <Column id="day" width={50} helpModalId="day">Dy</Column>
                      <Column id="hour" width={50} helpModalId="hour">Hr</Column>
                      <Column id="minute" width={50} helpModalId="minute">Mn</Column>
                      <Column id="second" width={75} helpModalId="second">Sec</Column>
                      <Column id="country" width={150} helpModalId="tsuCountry">Country</Column>
                      <Column id="eventValidity" width={85} helpModalId="tsuValidity">Tsunami Event Validity</Column>
                      <Column id="causeCode" width={85} helpModalId="tsuCauseCode">Tsunami Cause Code</Column>
                      <Column id="oceanicTsunami" width={85} helpModalId="tsuOceanicTsunami" cell={({ value }) => (value ? 'Y' : 'N')}>Oceanic Tsunami</Column>
                      <Column id="locationName" width={200} helpModalId="tsuLocationName">Location</Column>
                      <Column id="latitude" width={100} helpModalId="tsuLatitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Latitude</Column>
                      <Column id="longitude" width={100} helpModalId="tsuLongitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Longitude</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>


              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Tsunami Parameters</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="maxWaterHeight" width={100} helpModalId="tsuMaxWaterHeight">Maximum Water Height (m)</Column>
                      <Column
                        id="numRunups"
                        width={100}
                        sortable
                        helpModalId="tsuRunups"
                        cell={({ value, row }) => (value ? renderRelatedRunupsLink(row.get('id'), value) : value)}
                      >
                        Number of Runups
                      </Column>
                      <Column
                        id="numDeposits"
                        width={100}
                        sortable
                        helpModalId="tsuDeposits"
                        cell={({ value, row }) => (value ? renderRelatedDepositsLink(row.get('id'), value) : '')}
                      >
                        Number of Deposits
                      </Column>
                      <Column id="tsMtAbe" width={100} helpModalId="tsuMagAbe">Tsunami Magnitude (Abe)</Column>
                      <Column id="tsMtIi" width={100} helpModalId="tsuMagIida">Tsunami Magnitude (Iida)</Column>
                      <Column id="tsIntensity" width={100} helpModalId="tsuIntensity">Tsunami Intensity</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Tsunami Effects</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="deaths" width={100} helpModalId="tsuDeaths">Deaths</Column>
                      <Column id="deathsAmountOrder" width={125} helpModalId="tsuDeathsDescription">Death Description</Column>
                      <Column id="missing" width={100} helpModalId="tsuMissing">Missing</Column>
                      <Column id="missingAmountOrder" width={125} helpModalId="tsuMissingDescription">Missing Description</Column>
                      <Column id="injuries" width={100} helpModalId="tsuInjuries">Injuries</Column>
                      <Column id="injuriesAmountOrder" width={125} helpModalId="tsuInjuriesDescription">Injuries Description</Column>
                      <Column id="damageMillionsDollars" width={100} helpModalId="tsuDamage">Damage ($Mil)</Column>
                      <Column id="damageAmountOrder" width={125} helpModalId="tsuDamageDescription">Damage Description</Column>
                      <Column id="housesDestroyed" width={100} helpModalId="tsuHousesDestroyed">Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrder" width={125} helpModalId="tsuHousesDestroyedDescription">Houses Destroyed Description</Column>
                      <Column id="housesDamaged" width={100} helpModalId="tsuHousesDamaged">Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrder" width={125} helpModalId="tsuHousesDamagedDescription">Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>


              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Total Effects (Earthquake and Tsunami, Volcano, etc.)</CardTitle>
                    <SingleResultGrid data={tsunami}>
                      <Column id="deathsTotal" width={100} helpModalId="tsuTotalDeaths">Total Deaths</Column>
                      <Column id="deathsAmountOrderTotal" width={125} helpModalId="tsuTotalDeathsDescription">Total Death Description</Column>
                      <Column id="missingTotal" width={100} helpModalId="tsuTotalMissing">Total Missing</Column>
                      <Column id="missingAmountOrderTotal" width={125} helpModalId="tsuTotalMissingDescription">Total Missing Description</Column>
                      <Column id="injuriesTotal" width={100} helpModalId="tsuTotalInjuries">Total Injuries</Column>
                      <Column id="injuriesAmountOrderTotal" width={125} helpModalId="tsuTotalInjuriesDescription">Total Injuries Description</Column>
                      <Column id="damageMillionsDollarsTotal" width={100} helpModalId="tsuTotalDamage">Total Damage ($Mil)</Column>
                      <Column id="damageAmountOrderTotal" width={125} helpModalId="tsuTotalDamageDescription">Total Damage Description</Column>
                      <Column id="housesDestroyedTotal" width={100} helpModalId="tsuTotalHousesDestroyed">Total Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrderTotal" width={125} helpModalId="tsuTotalHousesDestroyedDescription">Total Houses Destroyed Description</Column>
                      <Column id="housesDamagedTotal" width={100} helpModalId="tsuTotalHousesDamaged">Total Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrderTotal" width={125} helpModalId="tsuTotalHousesDamagedDescription">Total Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">
                      <TitleTooltip label="Comments" helpModalId="commentDisclaimer" />
                    </CardTitle>
                    <CommentsSection title="">{tsunami.get('comments')}</CommentsSection>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section haz-reference-info">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">References for the Tsunami Event</CardTitle>
                    <ReferencesTable
                      references={references}
                      handleToggleColumns={this.handleToggleColumns}
                      sort={sort}
                      visibleColumns={visibleColumns}
                    />
                  </CardBody>
                </Card>
              </div>

            </div>

          </CardBody>
        </Card>


      </div>
    );
  }
}


TsunamiInfoPageUnwrapped.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string }) }).isRequired,
  fetched: PropTypes.bool.isRequired,
  clearTsunamiEventInfo: PropTypes.func.isRequired,
  getTsunamiEventInfo: PropTypes.func.isRequired,
  getTsunamiEventReferences: PropTypes.func.isRequired,
  tsunami: ImmutablePropTypes.map.isRequired,
  references: ImmutablePropTypes.list.isRequired,
  sortReferences: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  authorities: ImmutablePropTypes.list.isRequired,
};

const mapStateToProps = (state) => {
  const fetchedTsunami = state.getIn(['tsunamiEventInfoPage', 'fetchedTsunami']);
  const fetchedReferences = state.getIn(['tsunamiEventInfoPage', 'fetchedReferences']);
  return {
    fetched: [fetchedTsunami, fetchedReferences].every(v => v),
    tsunami: state.getIn(['tsunamiEventInfoPage', 'tsunami']),
    references: state.getIn(['tsunamiEventInfoPage', 'references']),
    sort: state.getIn(['tsunamiEventInfoPage', 'sort']),
    visibleColumns: state.getIn(['tsunamiEventInfoPage', 'visibleColumns']),
    sortReferences: PropTypes.func.isRequired,
    toggleColumns: PropTypes.func.isRequired,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  clearTsunamiEventInfo: clearTsunamiEventInfoAc,
  getTsunamiEventInfo: getTsunamiEventInfoAc,
  getTsunamiEventReferences: getTsunamiEventReferencesAc,
  sortReferences: sortReferencesAc,
  toggleColumns: toggleColumnsAc,
}, dispatch);

export const TsunamiInfoPage = connect(mapStateToProps, mapDispatchToProps)(TsunamiInfoPageUnwrapped);
